export interface DivCard {
  name: string;
  icon?: string;
  stackSize: number;
  cardPrice: number;       // divine per card
  cardPriceChaos: number;
  totalCost: number;       // stackSize × cardPrice
  totalCostChaos: number;
  rewardValue: number;     // divine
  rewardValueChaos: number;
  rewardText: string;
  rewardType: "currency" | "gem" | "unique" | "unknown";
  profit: number;          // rewardValue - totalCost
  profitPct: number;
  count: number;
}

// Parse reward from explicitModifiers text
// e.g. "<currencyitem>{Divine Orb}" or "<uniqueitem>{Headhunter}" or "<gemitem>{Empower Support}"
function parseReward(text: string): { type: string; name: string } | null {
  const m = text.match(/<(\w+)>\{([^}]+)\}/);
  if (!m) return null;
  return { type: m[1].toLowerCase(), name: m[2] };
}

export async function fetchDivCards(league = "Allflame"): Promise<DivCard[]> {
  const res = await fetch(`/api/divcards?league=${encodeURIComponent(league)}`);
  const data = await res.json();

  const cards: any[]   = data.cards ?? [];
  const gemPrices      = data.gemPrices ?? {};
  const uniquePrices   = data.uniquePrices ?? {};
  const currencyPrices = data.currencyPrices ?? {};

  // Divine price from chaos equivalent
  const divChaos = data.divChaos ?? currencyPrices["divine orb"]?.chaos ?? 267;

  const results: DivCard[] = [];

  for (const card of cards) {
    if (!card.stackSize || card.stackSize < 1) continue;

    // Always prefer divineValue; use chaosValue only if < 1 divine
    const cardPriceDiv   = (card.divineValue ?? 0) >= 1
      ? (card.divineValue ?? 0)
      : (card.chaosValue ?? 0) / divChaos;
    const cardPriceChaos = cardPriceDiv >= 1
      ? cardPriceDiv * divChaos
      : (card.chaosValue ?? 0);

    const totalCost      = cardPriceDiv * card.stackSize;
    const totalCostChaos = cardPriceChaos * card.stackSize;

    // Parse reward
    const modText = (card.explicitModifiers?.[0]?.text ?? "") as string;
    const reward  = parseReward(modText);
    if (!reward) continue;

    let rewardValue = 0;
    let rewardValueChaos = 0;
    let rewardType: DivCard["rewardType"] = "unknown";
    const rName = reward.name.toLowerCase();

    if (reward.type === "currencyitem") {
      const p = currencyPrices[rName];
      if (p) {
        rewardValue      = p.div >= 1 ? p.div : p.chaos / divChaos;
        rewardValueChaos = p.chaos;
        rewardType = "currency";
      }
    } else if (reward.type === "gemitem") {
      const p = gemPrices[rName];
      if (p) {
        rewardValue      = p.div >= 1 ? p.div : p.chaos / divChaos;
        rewardValueChaos = p.chaos;
        rewardType = "gem";
      }
    } else if (reward.type === "uniqueitem") {
      const p = uniquePrices[rName];
      if (p) {
        rewardValue      = p.div >= 1 ? p.div : p.chaos / divChaos;
        rewardValueChaos = p.chaos;
        rewardType = "unique";
      }
    }

    if (rewardValue === 0) continue;

    const profit    = rewardValue - totalCost;
    const profitPct = totalCost > 0 ? (profit / totalCost) * 100 : 0;

    results.push({
      name: card.name,
      icon: card.icon,
      stackSize: card.stackSize,
      cardPrice: cardPriceDiv,
      cardPriceChaos,
      totalCost,
      totalCostChaos,
      rewardValue,
      rewardValueChaos,
      rewardText: reward.name,
      rewardType,
      profit,
      profitPct,
      count: card.count ?? 0,
    });
  }

  results.sort((a, b) => b.profit - a.profit);
  return results;
}
